// Effective common view for the current screen: the mockup effectiveScope / teamFilterOn / teamPickable / App.filter /
// App.condText rules applied to the signed-in person's permissions and the route's scope permission (handle.scopePerm).
import { useMemo } from 'react';
import { useMatches } from 'react-router';
import type { QueryValue } from '../api/client';
import { useTeamsTree } from '../api/shell';
import { usePerms, useAuth } from '../auth/AuthContext';
import {
  DEFAULT_SCOPE_PERM,
  allowedScopes,
  effectiveScope,
  teamFilterOn,
  type MenuId,
  type TeamRef,
} from '../auth/permissions';
import { useFmt, useT } from '../i18n';
import { isCustomPeriod, periodRange, type Period } from '../lib/time';
import { useViewStore } from '../state/store';
import type { Method, ViewScope } from '../state/url';

/** Route metadata (react-router `handle`). */
export interface RouteHandle {
  /** Menu entry the route belongs to (decides visibility and the current item). */
  menuId?: MenuId;
  /** i18n key of the screen name (header title, document title, h1 of state screens). */
  titleKey: string;
  /** Permission key that decides the scope choices on this screen (default usage.view; activity log = logs.list). */
  scopePerm?: string;
  /** Extra permission(s) — any one is enough (e.g. person details need people.individual). */
  perm?: string | readonly string[];
}

export function useRouteHandle(): RouteHandle | undefined {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const h = matches[i]!.handle as RouteHandle | undefined;
    if (h && typeof h === 'object' && 'titleKey' in h) return h;
  }
  return undefined;
}

/** Conditions an API query receives (App.filter): scope · period · method · teamId · userId (+ screen extras). */
export interface ViewFilter {
  scope: ViewScope;
  period: Period;
  method?: Exclude<Method, 'all'>;
  teamId?: string;
  userId?: string;
  [extra: string]: unknown;
}

export type ApiQuery = Record<string, QueryValue | readonly QueryValue[]>;

/** ViewFilter key → API query key (contracts CommonFilterQuery: `team` = team ids, `user` = one person). */
const QUERY_KEY: Readonly<Record<string, string>> = { teamId: 'team', userId: 'user' };

/**
 * A view filter as API query values (plan 10.3.5, contracts CommonFilterQuery): `period=7d`, or
 * `period=custom&from=YYYY-MM-DD&to=YYYY-MM-DD` for a picked range; teamId → `team`, userId → `user`; other keys as they are
 * (lists stay lists).
 */
export function filterQuery(f: ViewFilter): ApiQuery {
  const q: ApiQuery = {};
  for (const [k0, v] of Object.entries(f)) {
    if (k0 === 'period' || v === undefined || v === null) continue;
    const k = QUERY_KEY[k0] ?? k0;
    if (Array.isArray(v)) q[k] = v.filter((x): x is string | number | boolean => ['string', 'number', 'boolean'].includes(typeof x));
    else if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') q[k] = v;
  }
  if (isCustomPeriod(f.period)) Object.assign(q, { period: 'custom', from: f.period.from, to: f.period.to });
  else q.period = f.period;
  return q;
}

export interface View {
  scope: ViewScope;
  allowed: ViewScope[];
  scopePerm: string;
  teamFilterOn: boolean;
  /** effective team filter (null when not usable here) */
  team: string | null;
  /** effective one-person filter (never in "me") */
  person: string | null;
  period: Period;
  method: Method;
  /** teams this person may pick in the team filter */
  pickableTeams: TeamRef[];
  /** teams of the header team filter (GET /api/v1/teams; `small` = not pickable for this person) */
  teams: TeamRef[];
  /** the team list is known (or not needed here) — until then a team from the address is kept unverified */
  teamsReady: boolean;
  filter(extra?: Record<string, unknown>): ViewFilter;
  /** filter() as API query values — apiFetch(path, { query: view.query() }). */
  query(extra?: Record<string, unknown>): ApiQuery;
}

export function useView(): View {
  const perms = usePerms();
  const { me } = useAuth();
  const handle = useRouteHandle();
  const scopePerm = handle?.scopePerm ?? DEFAULT_SCOPE_PERM;
  const scopeWanted = useViewStore((s) => s.scopeWanted);
  const team = useViewStore((s) => s.team);
  const person = useViewStore((s) => s.person);
  const period = useViewStore((s) => s.period);
  const method = useViewStore((s) => s.method);
  const filterPossible = !!me && teamFilterOn(perms, scopePerm, effectiveScope(perms, scopePerm, scopeWanted));
  const tree = useTeamsTree(filterPossible);
  return useMemo(() => {
    const allowed = allowedScopes(perms, scopePerm);
    const scope = effectiveScope(perms, scopePerm, scopeWanted);
    const on = teamFilterOn(perms, scopePerm, scope);
    const teamsReady = !on || tree.isSuccess || tree.isError;
    // the server decides who may pick a small team (people.identity 'all'); groups (본부) are not filter targets
    const scopeTeamIds = new Set(me?.scopeTeams.map((tm) => tm.id) ?? []);
    const teams: TeamRef[] = (tree.data?.teams ?? []).filter((n) => !n.archived && !n.group && (scope !== 'team' || scopeTeamIds.has(n.id))).map((n) => ({ id: n.id, name: n.name, small: !n.pickable }));
    const pickableTeams = teams.filter((tm) => !tm.small);
    const effTeam = on && team && (!teamsReady || pickableTeams.some((x) => x.id === team)) ? team : null;
    const effPerson = scope !== 'me' ? person : null;
    const filter = (extra?: Record<string, unknown>): ViewFilter => {
      const f: ViewFilter = { scope, period };
      if (method !== 'all') f.method = method;
      if (effTeam) f.teamId = effTeam;
      if (effPerson) f.userId = effPerson;
      if (extra)
        for (const k of Object.keys(extra)) {
          if (extra[k] === undefined) delete f[k];
          else f[k] = extra[k];
        }
      return f;
    };
    const query = (extra?: Record<string, unknown>): ApiQuery => filterQuery(filter(extra));
    return { scope, allowed, scopePerm, teamFilterOn: on, team: effTeam, person: effPerson, period, method, pickableTeams, teams, teamsReady, filter, query };
  }, [perms, me?.scopeTeams, scopePerm, scopeWanted, team, person, period, method, tree.data, tree.isSuccess, tree.isError]);
}

/** Card condition text (App.condText): "최근 7일, CLI 도구, 플랫폼팀" — a picked range shows its dates. */
export function useCondText() {
  const t = useT();
  const fmt = useFmt();
  const view = useView();
  return (f?: Partial<ViewFilter>): string => {
    const per = f?.period ?? view.period;
    const parts: string[] = [];
    if (isCustomPeriod(per)) {
      const pr = periodRange(per);
      parts.push(fmt.range(pr.from, Math.min(pr.to, Date.now()) - 1));
    } else parts.push(t('period.long.' + per));
    const m = f?.method ?? (view.method !== 'all' ? view.method : undefined);
    if (m) parts.push(t('method.' + m));
    const teamId = f?.teamId ?? view.team;
    const tm = teamId ? view.teams.find((x) => x.id === teamId) : null;
    if (tm) parts.push(tm.name);
    return parts.join(', ');
  };
}
